import { Link } from "react-router-dom";
import { Eye, MapPin } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { formatPrice } from "@/components/ListingCard";
import { ListingCardActions } from "@/components/ListingCardActions";
import { VerifiedProBadge } from "@/components/VerifiedProBadge";
import { ProgressiveImage } from "@/components/ProgressiveImage";
import { Button } from "@/components/ui/button";
import { categoryLabel } from "@/lib/categoryLabel";

/** Popular listing card with views emphasis for home feed. */
export function PopularListingCard({ listing, testId }) {
  const { t } = useLang();
  const img = listing.images?.[0];
  const seller = listing.seller || {};
  const views = listing.views_count || 0;

  return (
    <article
      data-testid={testId}
      className="matpass-card-hover group flex h-full flex-col overflow-hidden rounded-2xl border border-matpro-border bg-white shadow-[0_4px_20px_rgba(24,24,27,0.05)] hover:border-matpro-green/40"
    >
      <Link to={`/annonce/${listing.id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden">
          {img ? (
            <ProgressiveImage
              src={img}
              alt={listing.title}
              className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
              wrapperClassName="h-full w-full"
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-zinc-100 text-xs text-zinc-400">{t("card.no_photo")}</div>
          )}
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-matpro-anthracite/90 px-2 py-1 text-[10px] font-bold text-white">
            <Eye className="h-3 w-3" />
            {views.toLocaleString("fr-BE")} {t("home.popular_views")}
          </span>
        </div>
      </Link>

      <ListingCardActions listing={listing} />

      <div className="flex flex-1 flex-col p-4">
        <p className="font-heading text-xl font-black text-matpro-green">{formatPrice(listing.price)}</p>
        <h3 className="mt-1 line-clamp-2 font-heading text-sm font-bold text-matpro-anthracite">{listing.title}</h3>
        {listing.category_slug || listing.category_label ? (
          <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-matpro-anthracite-muted">
            {categoryLabel(listing.category_slug, t, listing.category_label)}
          </p>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-matpro-anthracite-muted">
          {(seller.company_name || seller.full_name) && (
            <span className="font-semibold text-matpro-anthracite">
              {seller.company_name || seller.full_name}
            </span>
          )}
          {seller.verified_pro ? <VerifiedProBadge size="xs" /> : null}
        </div>
        {listing.city ? (
          <p className="mt-1 flex items-center gap-1 text-xs text-matpro-anthracite-muted">
            <MapPin className="h-3 w-3 text-matpro-green" />
            {listing.city}
          </p>
        ) : null}
        <Button
          asChild
          variant="outline"
          className="mt-4 h-9 w-full rounded-xl border-matpro-border font-semibold hover:border-matpro-green hover:text-matpro-green"
        >
          <Link to={`/annonce/${listing.id}`}>{t("home.view_listing")}</Link>
        </Button>
      </div>
    </article>
  );
}
